import {
  CompareArrows as CompareIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchActivePackages,
  searchPackages,
} from "../../../application/actions/packageActions";
import {
  selectPackagesData,
  selectPackageSearchData,
  selectPackageSearchLoading,
  selectPackageSearchTotalCount,
  selectPackagesLoading,
} from "../../../application/selectors/packages";

const PackageList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const packages = useSelector(selectPackagesData);
  const loading = useSelector(selectPackagesLoading);
  const searchResults = useSelector(selectPackageSearchData);
  const searchLoading = useSelector(selectPackageSearchLoading);
  const totalCount = useSelector(selectPackageSearchTotalCount);

  // Local State
  const [filters, setFilters] = useState({
    searchTerm: "",
    minCapacity: "",
    maxCapacity: "",
    minPrice: "",
    maxPrice: "",
    city: "",
    sortBy: "createdAt",
    sortDescending: true,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 12,
  });

  const [selectedPackages, setSelectedPackages] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Load initial data
  useEffect(() => {
    loadPackages();
  }, [pagination.pageNumber, pagination.pageSize]);

  const loadPackages = () => {
    const params = {
      ...pagination,
    };
    dispatch(fetchActivePackages(params));
  };

  const handleSearch = () => {
    const searchParams = {
      ...filters,
      ...pagination,
      pageNumber: 1, // Reset to first page on search
    };
    dispatch(searchPackages(searchParams));
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      pageNumber: value,
    });
    window.scrollTo(0, 0);
  };

  const handlePackageSelect = (packageId) => {
    if (selectedPackages.includes(packageId)) {
      setSelectedPackages(selectedPackages.filter((id) => id !== packageId));
    } else {
      if (selectedPackages.length >= 4) {
        toast.warning("حداکثر ۴ پکیج را می‌توانید مقایسه کنید");
        return;
      }
      setSelectedPackages([...selectedPackages, packageId]);
    }
  };

  const handleCompare = () => {
    if (selectedPackages.length < 2) {
      toast.warning("لطفاً حداقل ۲ پکیج را انتخاب کنید");
      return;
    }
    navigate(`/packages/compare`, {
      state: { packageIds: selectedPackages },
    });
  };

  const handleViewDetails = (packageId) => {
    navigate(`/packages/${packageId}`);
  };

  const handleClearFilters = () => {
    setFilters({
      searchTerm: "",
      minCapacity: "",
      maxCapacity: "",
      minPrice: "",
      maxPrice: "",
      city: "",
      sortBy: "createdAt",
      sortDescending: true,
    });
    loadPackages();
  };

  const displayPackages =
    searchResults && searchResults.length > 0 ? searchResults : packages;

  const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          پکیج‌های خورشیدی
        </Typography>
        <Typography variant="body1" color="text.secondary">
          مشاهده و مقایسه پکیج‌های مختلف سیستم خورشیدی
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Box */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="جستجو در پکیج‌ها..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid>

          {/* Filter Toggle */}
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? "پنهان کردن فیلترها" : "نمایش فیلترها"}
            </Button>
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              جستجو
            </Button>
          </Grid>

          {/* Advanced Filters */}
          {showFilters && (
            <>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="حداقل ظرفیت (kW)"
                  value={filters.minCapacity}
                  onChange={(e) =>
                    handleFilterChange("minCapacity", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="حداکثر ظرفیت (kW)"
                  value={filters.maxCapacity}
                  onChange={(e) =>
                    handleFilterChange("maxCapacity", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="حداقل قیمت (ریال)"
                  value={filters.minPrice}
                  onChange={(e) =>
                    handleFilterChange("minPrice", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="حداکثر قیمت (ریال)"
                  value={filters.maxPrice}
                  onChange={(e) =>
                    handleFilterChange("maxPrice", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="شهر"
                  value={filters.city}
                  onChange={(e) => handleFilterChange("city", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>مرتب‌سازی بر اساس</InputLabel>
                  <Select
                    value={filters.sortBy}
                    label="مرتب‌سازی بر اساس"
                    onChange={(e) =>
                      handleFilterChange("sortBy", e.target.value)
                    }
                  >
                    <MenuItem value="createdAt">تاریخ ایجاد</MenuItem>
                    <MenuItem value="price">قیمت</MenuItem>
                    <MenuItem value="capacity">ظرفیت</MenuItem>
                    <MenuItem value="name">نام</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearFilters}
                >
                  پاک کردن فیلترها
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Compare Bar */}
      {selectedPackages.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body1">
            {selectedPackages.length} پکیج انتخاب شده
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<CompareIcon />}
              onClick={handleCompare}
              disabled={selectedPackages.length < 2}
              sx={{ mr: 1 }}
            >
              مقایسه پکیج‌ها
            </Button>
            <Button variant="outlined" onClick={() => setSelectedPackages([])}>
              پاک کردن انتخاب‌ها
            </Button>
          </Box>
        </Paper>
      )}

      {/* Loading State */}
      {(loading || searchLoading) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Packages Grid */}
      {!loading && !searchLoading && (
        <>
          {displayPackages && displayPackages.length > 0 ? (
            <Grid container spacing={3}>
              {displayPackages.map((pkg) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={pkg.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                      position: "relative",
                    }}
                  >
                    {/* Compare Checkbox */}
                    <Checkbox
                      checked={selectedPackages.includes(pkg.id)}
                      onChange={() => handlePackageSelect(pkg.id)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        backgroundColor: "white",
                        borderRadius: 1,
                        zIndex: 1,
                      }}
                    />

                    {/* Package Image */}
                    <CardMedia
                      component="img"
                      height="200"
                      image={pkg.imageUrl || "/default-package.jpg"}
                      alt={pkg.name}
                      sx={{ objectFit: "cover" }}
                    />

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom noWrap>
                        {pkg.name}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={`${pkg.capacity} کیلووات`}
                          size="small"
                          color="primary"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Chip
                          label={pkg.isActive ? "فعال" : "غیرفعال"}
                          size="small"
                          color={pkg.isActive ? "success" : "default"}
                          sx={{ mb: 1 }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {pkg.description}
                      </Typography>

                      {/* نمایش تعداد تجهیزات */}
                      {pkg.equipments && pkg.equipments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            تجهیزات: {pkg.equipments.length} قلم
                          </Typography>
                        </Box>
                      )}

                      {pkg.equipmentList && pkg.equipmentList.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            تجهیزات: {pkg.equipmentList.length} قلم
                          </Typography>
                        </Box>
                      )}

                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{ fontWeight: "bold", mb: 2 }}
                      >
                        {pkg.totalPrice?.toLocaleString("fa-IR")} ریال
                      </Typography>

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewDetails(pkg.id)}
                      >
                        مشاهده جزئیات
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                پکیجی یافت نشد
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                لطفاً فیلترهای جستجوی خود را تغییر دهید
              </Typography>
            </Paper>
          )}

          {/* Pagination */}
          {displayPackages && displayPackages.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={pagination.pageNumber}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default PackageList;
